import React, { useEffect, useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import api from '../utils/api';
import './FinancialStatusManagementPopover.css';

function FinancialStatusManagementPopover({ onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [form, setForm] = useState({
    id: null,
    offertype: '',
    financial_status: '',
    semester_financial_status: '',
  });

  const resetForm = () => {
    setForm({
      id: null,
      offertype: '',
      financial_status: '',
      semester_financial_status: '',
    });
  };

  const loadItems = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await api.get('/api/admin/financial-statuses');
      setItems(response.data || []);
    } catch (err) {
      console.error('Failed to load financial status mappings:', err);
      setError(err.response?.data?.detail || 'Pénzügyi státuszok betöltése sikertelen');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError('');
      const payload = {
        id: form.id,
        offertype: form.offertype.trim() || null,
        financial_status: form.financial_status.trim() || null,
        semester_financial_status: form.semester_financial_status.trim() || null,
      };

      let response;
      if (payload.id) {
        response = await api.put(`/api/admin/financial-statuses/${payload.id}`, payload);
      } else {
        response = await api.post('/api/admin/financial-statuses', payload);
      }

      const saved = response.data;
      setItems((prev) => {
        const others = prev.filter((i) => i.id !== saved.id);
        return [...others, saved].sort((a, b) => a.id - b.id);
      });

      resetForm();
      setShowAddForm(false);
    } catch (err) {
      console.error('Failed to save financial status mapping:', err);
      setError(err.response?.data?.detail || 'Mentés sikertelen');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (item) => {
    setForm({
      id: item.id,
      offertype: item.offertype || '',
      financial_status: item.financial_status || '',
      semester_financial_status: item.semester_financial_status || '',
    });
    setShowAddForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Biztosan törölni szeretné ezt a pénzügyi státusz-mappolást?')) return;
    try {
      setDeletingId(id);
      await api.delete(`/api/admin/financial-statuses/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
    } catch (err) {
      console.error('Failed to delete financial status mapping:', err);
      alert(err.response?.data?.detail || 'Törlés sikertelen');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="fin-mgmt-overlay">
      <div className="fin-mgmt-popover">
        <div className="fin-mgmt-header">
          <h2>Pénzügyi státuszok</h2>
          <button className="icon-button" onClick={onClose}>
            <X size={18} />
          </button>
        </div>

        {error && <div className="fin-mgmt-error">{error}</div>}

        <div className="fin-mgmt-toolbar">
          <button
            className="add-item-button"
            type="button"
            onClick={() => {
              resetForm();
              setShowAddForm(true);
            }}
          >
            <Plus size={16} />
            Új státusz
          </button>
        </div>

        {showAddForm && (
          <div className="fin-mgmt-add-form-wrapper">
            <form className="fin-mgmt-add-form" onSubmit={handleSave}>
              <div className="form-row">
                <label>Offer type</label>
                <input
                  type="text"
                  name="offertype"
                  value={form.offertype}
                  onChange={handleChange}
                  disabled={saving}
                />
              </div>
              <div className="form-row">
                <label>Pénzügyi státusz</label>
                <input
                  type="text"
                  name="financial_status"
                  value={form.financial_status}
                  onChange={handleChange}
                  disabled={saving}
                />
              </div>
              <div className="form-row">
                <label>Félév pénzügyi státusz</label>
                <input
                  type="text"
                  name="semester_financial_status"
                  value={form.semester_financial_status}
                  onChange={handleChange}
                  disabled={saving}
                />
              </div>
              <div className="fin-mgmt-form-actions">
                <button
                  type="button"
                  className="secondary-button"
                  onClick={() => setShowAddForm(false)}
                  disabled={saving}
                >
                  Mégse
                </button>
                <button type="submit" className="primary-button" disabled={saving}>
                  {saving ? 'Mentés...' : 'Mentés'}
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="fin-mgmt-list">
          {loading ? (
            <div className="fin-mgmt-loading">Betöltés...</div>
          ) : items.length === 0 ? (
            <div className="fin-mgmt-empty">Még nincsenek pénzügyi státusz-mappolások.</div>
          ) : (
            items
              .slice()
              .sort((a, b) => a.id - b.id)
              .map((item) => (
                <div key={item.id} className="fin-mgmt-item">
                  <div className="fin-mgmt-content" onDoubleClick={() => handleEdit(item)}>
                    <div className="fin-mgmt-title">
                      <span className="fin-mgmt-offertype">{item.offertype || '-'}</span>
                    </div>
                    <div className="fin-mgmt-readonly-grid">
                      <div className="fin-mgmt-readonly-field">
                        <label>Pénzügyi státusz</label>
                        <input type="text" value={item.financial_status || ''} disabled />
                      </div>
                      <div className="fin-mgmt-readonly-field">
                        <label>Félév pénzügyi státusz</label>
                        <input type="text" value={item.semester_financial_status || ''} disabled />
                      </div>
                    </div>
                  </div>
                  <div className="fin-mgmt-actions">
                    <button type="button" className="icon-button" onClick={() => handleEdit(item)}>
                      Szerkesztés
                    </button>
                    <button
                      type="button"
                      className="icon-button danger"
                      onClick={() => handleDelete(item.id)}
                      disabled={deletingId === item.id}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))
          )}
        </div>
      </div>
    </div>
  );
}

export default FinancialStatusManagementPopover;
